import React, { useState } from 'react';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { bpsToPercent, formatUSDT, parseUSDT, pctToBps } from '../../utils/format';

const FeeManager = ({ fees, limits, dailyLimit, onChange }) => {
  const { contract, isOwner } = useWeb3();

  const [claim, setClaim] = useState('');
  const [early, setEarly] = useState('');
  const [adminFee, setAdminFee] = useState('');
  const [minInv, setMinInv] = useState('');
  const [multiple, setMultiple] = useState('');
  const [dailyLim, setDailyLim] = useState('');
  const [busy, setBusy] = useState('');

  const send = async (label, fn) => {
    if (!contract) return;
    setBusy(label);
    let lid = null;
    try {
      lid = notify.loading(`${label}...`);
      const tx = await fn();
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success(`${label} updated`);
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <Card title="Fees & Limits" subtitle="Tune protocol fees and investment bounds">
      <div className="grid grid-2">
        <div>
          <Input
            label={`Claim Fee (current ${bpsToPercent(fees?.claim || 0)}%)`}
            value={claim}
            onChange={(e) => setClaim(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="e.g. 10"
            suffix="%"
            helper="Max 50%"
          />
          <Button
            variant="primary"
            size="sm"
            block
            loading={busy === 'claim-fee'}
            onClick={() => send('claim-fee', () => contract.updateClaimFee(pctToBps(claim)))}
            disabled={!claim}
          >Update Claim Fee</Button>
        </div>

        <div>
          <Input
            label={`Early Withdraw Fee (current ${bpsToPercent(fees?.early || 0)}%)`}
            value={early}
            onChange={(e) => setEarly(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="e.g. 20"
            suffix="%"
            helper="Max 50%"
          />
          <Button
            variant="primary"
            size="sm"
            block
            loading={busy === 'early-fee'}
            onClick={() => send('early-fee', () => contract.updateEarlyWithdrawFee(pctToBps(early)))}
            disabled={!early}
          >Update Early Fee</Button>
        </div>

        <div>
          <Input
            label={`Minimum Investment (current ${formatUSDT(limits?.min || 0)} USDT)`}
            value={minInv}
            onChange={(e) => setMinInv(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="USDT amount"
            suffix="USDT"
          />
          <Button
            variant="primary"
            size="sm"
            block
            loading={busy === 'min-inv'}
            onClick={() => send('min-inv', () => contract.updateMinimumInvestment(parseUSDT(minInv)))}
            disabled={!minInv}
          >Update Minimum</Button>
        </div>

        <div>
          <Input
            label={`Investment Multiple (current ${formatUSDT(limits?.multiple || 0)} USDT)`}
            value={multiple}
            onChange={(e) => setMultiple(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="USDT step"
            suffix="USDT"
          />
          <Button
            variant="primary"
            size="sm"
            block
            loading={busy === 'multiple'}
            onClick={() => send('multiple', () => contract.updateInvestmentMultiple(parseUSDT(multiple)))}
            disabled={!multiple}
          >Update Multiple</Button>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <Input
            label={`Daily Withdraw Cap (current ${
              dailyLimit && dailyLimit !== '0' ? `${formatUSDT(dailyLimit)} USDT` : 'Disabled'
            })`}
            value={dailyLim}
            onChange={(e) => setDailyLim(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="USDT amount (0 to disable)"
            suffix="USDT"
            helper="Per-user 24h cap on claimRewards. Set 0 to disable."
          />
          <Button
            variant="primary"
            size="sm"
            block
            loading={busy === 'daily-limit'}
            onClick={() => send('daily-limit', () => contract.updateDailyWithdrawLimit(parseUSDT(dailyLim || '0')))}
            disabled={dailyLim === ''}
          >Update Daily Limit</Button>
        </div>

        {isOwner && (
          <div style={{ gridColumn: '1 / -1' }}>
            <Input
              label={`Admin Wallet Fee (current ${bpsToPercent(fees?.adminWallet || 0)}%)`}
              value={adminFee}
              onChange={(e) => setAdminFee(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="e.g. 80"
              suffix="%"
              helper="Max 100% · Owner only"
            />
            <Button
              variant="primary"
              size="sm"
              block
              loading={busy === 'admin-fee'}
              onClick={() => send('admin-fee', () => contract.updateAdminWalletFee(pctToBps(adminFee)))}
              disabled={!adminFee}
            >Update Admin Fee</Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default FeeManager;
